/**
 * scoreOverlay.ts — pure SVG geometry for the post-session review overlay (#6).
 *
 * After a session stops, the console draws the target melody on top of what the
 * user actually sang (목표 vs 실제). This module turns a `TargetMelody` + the
 * captured actual-pitch points into ready-to-render SVG coordinates. It is pure:
 * no React/native, deterministic, never throws — actual points may carry null /
 * NaN midi (unvoiced), so every span degrades to a gap.
 *
 * The actual curve here is DISPLAY-ONLY (raw `frame.midi` for visual comparison);
 * the score itself is still computed by the pure `@muzly/shared` core elsewhere.
 */

import { PITCH_CONFIDENCE_MIN } from '@muzly/shared';
import type { TargetMelody } from '@muzly/shared';
import { clampNum } from '@muzly/voice-engine';

const VIEW_W = 600;
const VIEW_H = 200;
const CONF_MIN = PITCH_CONFIDENCE_MIN;
const MIN_MIDI_SPAN = 12; // 최소 1옥타브 세로 범위 — 음 하나여도 납작하지 않게
const MIDI_PAD = 2; // 위아래 여백(semitone)
const FALLBACK_MIDI_MIN = 48; // 데이터가 전혀 없을 때 C3
const FALLBACK_MIDI_MAX = 72; // C5

/** 오버레이에 그릴 실제 음높이 한 점 — midi(null=unvoiced) + 신뢰도(흐림). */
export type OverlayPoint = {
  atMs: number;
  midi: number | null;
  confidence: number;
};

/** 목표 음 한 칸 — 곡 타임라인 [start,end)를 해당 midi 높이의 수평선으로. */
export type OverlayTargetLine = {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
};

/** 실제 곡선의 연속 구간(gap에서 끊김) — polyline points + 평균 opacity. */
export type OverlaySegment = {
  points: string;
  opacity: number;
};

export type ScoreOverlay = {
  width: number;
  height: number;
  durationMs: number;
  midiMin: number;
  midiMax: number;
  targetLines: OverlayTargetLine[];
  actualSegments: OverlaySegment[];
  /** X of the current-time playhead (live view), or null when not requested. */
  playheadX: number | null;
};

export type BuildOverlayInput = {
  melody: TargetMelody;
  actual: OverlayPoint[];
  width?: number;
  height?: number;
  confMin?: number;
  /** Song-time of the live playhead; omit (post-session review) ⇒ no playhead. */
  playheadMs?: number;
};

/** 곡 길이 = max(마지막 음 끝, 마지막 실제 프레임 시각, 1) — 0 나눗셈 방지. */
const overlayDurationMs = (
  melody: TargetMelody,
  actual: OverlayPoint[],
): number => {
  const lastNote = melody.notes.at(-1);
  const noteEnd = lastNote ? lastNote.startMs + lastNote.durMs : 0;
  const lastActual = actual.at(-1)?.atMs ?? 0;
  return Math.max(1, noteEnd, lastActual);
};

/** 목표·실제 양쪽 midi를 모아 여백을 둔 세로 범위(최소 1옥타브 보장). */
const overlayMidiRange = (
  melody: TargetMelody,
  actual: OverlayPoint[],
): { midiMin: number; midiMax: number } => {
  const midis = [
    ...melody.notes.map((n) => n.midi),
    ...actual
      .map((p) => p.midi)
      .filter((m): m is number => typeof m === 'number' && Number.isFinite(m)),
  ];
  if (midis.length === 0) {
    return { midiMin: FALLBACK_MIDI_MIN, midiMax: FALLBACK_MIDI_MAX };
  }
  let lo = Math.floor(Math.min(...midis)) - MIDI_PAD;
  let hi = Math.ceil(Math.max(...midis)) + MIDI_PAD;
  if (hi - lo < MIN_MIDI_SPAN) {
    const grow = Math.ceil((MIN_MIDI_SPAN - (hi - lo)) / 2);
    lo -= grow;
    hi += grow;
  }
  return { midiMin: lo, midiMax: hi };
};

/** confidence(0~1) → stroke-opacity(0.3~1), 낮을수록 흐리게. */
const opacityOf = (confidence: number): number =>
  0.3 + 0.7 * clampNum(confidence, 0, 1);

/** unvoiced(midi=null)·저신뢰 지점에서 끊어 연속 구간들로 분할. */
const splitActualSegments = (
  actual: OverlayPoint[],
  confMin: number,
): OverlayPoint[][] => {
  const segments: OverlayPoint[][] = [];
  let current: OverlayPoint[] = [];
  for (const p of actual) {
    if (p.midi == null || !Number.isFinite(p.midi) || p.confidence < confMin) {
      if (current.length) {
        segments.push(current);
      }
      current = [];
      continue;
    }
    current.push(p);
  }
  if (current.length) {
    segments.push(current);
  }
  return segments;
};

/**
 * 목표 멜로디 + 실제 음높이를 SVG 좌표로. 순수/결정론, 절대 throw 안 함.
 */
export const buildScoreOverlay = ({
  melody,
  actual,
  width = VIEW_W,
  height = VIEW_H,
  confMin = CONF_MIN,
  playheadMs,
}: BuildOverlayInput): ScoreOverlay => {
  const durationMs = overlayDurationMs(melody, actual);
  const { midiMin, midiMax } = overlayMidiRange(melody, actual);
  const span = midiMax - midiMin;

  const mapX = (atMs: number): number =>
    (width * Math.min(durationMs, Math.max(0, atMs))) / durationMs;
  const mapY = (midi: number): number =>
    height *
    (1 - (Math.min(midiMax, Math.max(midiMin, midi)) - midiMin) / span);

  const targetLines: OverlayTargetLine[] = melody.notes.map((n) => ({
    x1: mapX(n.startMs),
    y1: mapY(n.midi),
    x2: mapX(n.startMs + n.durMs),
    y2: mapY(n.midi),
  }));

  const actualSegments: OverlaySegment[] = splitActualSegments(
    actual,
    confMin,
  ).map((seg) => ({
    points: seg
      .map(
        (p) =>
          `${mapX(p.atMs).toFixed(1)},${mapY(p.midi as number).toFixed(1)}`,
      )
      .join(' '),
    opacity: opacityOf(
      seg.reduce((sum, p) => sum + p.confidence, 0) / seg.length,
    ),
  }));

  return {
    width,
    height,
    durationMs,
    midiMin,
    midiMax,
    targetLines,
    actualSegments,
    playheadX: playheadMs === undefined ? null : mapX(playheadMs),
  };
};
